import React, { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import axios from "axios";
import { generateApiKey } from "../../../../helpers/common.helpers";
import { ExternalLink, Briefcase } from "lucide-react";
import LockedCard from "../LockedCard";
import { useNavigate } from "react-router-dom";

// Custom Card Components to replace Shadcn Card
const Card = ({ children, className = "" }) => {
  return (
    <div
      className={`rounded-lg border bg-card text-card-foreground shadow-sm ${className}`}
    >
      {children}
    </div>
  );
};

const CardContent = ({ children, className = "" }) => {
  return <div className={`p-6 pt-0 ${className}`}>{children}</div>;
};

const CardHeader = ({ children, className = "" }) => {
  return (
    <div className={`flex flex-col space-y-1.5 p-6 ${className}`}>
      {children}
    </div>
  );
};

const CardTitle = ({ children, className = "" }) => {
  return (
    <h3
      className={`text-2xl font-semibold leading-none tracking-tight ${className}`}
    >
      {children}
    </h3>
  );
};

// Custom Button Component to replace Shadcn Button
const Button = ({
  variant = "default",
  size = "default",
  className = "",
  children,
  ...props
}) => {
  const baseClasses =
    "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";

  const variantClasses = {
    default: "bg-primary text-primary-foreground hover:bg-primary/90",
    outline:
      "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
    ghost: "hover:bg-accent hover:text-accent-foreground",
    secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
  };

  const sizeClasses = {
    default: "h-10 px-4 py-2",
    sm: "h-9 rounded-md px-3",
    lg: "h-11 rounded-md px-8",
  };

  return (
    <button
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      {...props}
    >
      {children}
    </button>
  );
};

const JobOpertunites = () => {
  // State for API data
  const navigate = useNavigate();
  const [apiJobOpportunities, setApiJobOpportunities] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const isLocked = localStorage.getItem("status") !== "active";
  const jobOpportunities = [
    {
      title: "Data Analyst",
      company: "TechCorp",
      salary: "$65k - $80k",
      type: "Junior",
      badge: "Remote",
    },
    {
      title: "ML Engineer",
      company: "DataFlow",
      salary: "$85k - $120k",
      type: "Mid",
      badge: "Hybrid",
    },
    {
      title: "Data Analyst",
      company: "TechCorp",
      salary: "$65k - $80k",
      type: "Senior",
      badge: "Remote",
    },
    {
      title: "ML Engineer",
      company: "DataFlow",
      salary: "$85k - $120k",
      type: "Hybrid",
      badge: "Hybrid",
    },
    {
      title: "Data Analyst",
      company: "TechCorp",
      salary: "$65k - $80k",
      type: "Remote",
      badge: "Remote",
    },
    {
      title: "ML Engineer",
      company: "DataFlow",
      salary: "$85k - $120k",
      type: "Hybrid",
      badge: "Hybrid",
    },
  ];

  // API Integration - Fetch job opportunities (runs in background)
  const fetchJobOpportunities = async () => {
    try {
      setJobsLoading(true);
      console.log("Fetching job opportunities..."); // Debug log

      const user_id = localStorage.getItem("user_id");
      const enrol_id = localStorage.getItem("enrol_id");
      const category = localStorage.getItem("category");
      const program_id = localStorage.getItem("program_id");

      const sendData = {
        user_id: user_id,
        enrol_id: enrol_id,
        category: category,
        program_id: program_id,
      };

      const options = {
        headers: {
          "Access-Control-Allow-Origin": "*",
          "Content-Type": "application/json",
          "page-index": generateApiKey(),
        },
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_ENDPOINT_NODE_PRODUCTION_DASHBOARD}/dashboardhomepage/fetchjobportalmapped`,
        JSON.stringify(sendData),
        options
      );

      console.log("Job opportunities response:", response.data); // Debug log

      if (response.data.status === 300) {
        setApiJobOpportunities([]);
      } else {
        setApiJobOpportunities(response.data.slice(0, 6));
        console.log("API Jobs fetched:", response.data.slice(0, 6)); // Debug log
      }
      setJobsLoading(false);
    } catch (error) {
      console.error("Error fetching job opportunities:", error);
      setJobsLoading(false);
    }
  };

  // Handle job click for API data
  // const handleJobClick = (job, index) => {
  //   if (apiJobOpportunities[index] && apiJobOpportunities[index].job_link) {
  //     window.open(apiJobOpportunities[index].job_link, "_blank");
  //   }
  // };

  const handleJobClick = (job) => {
    if (job.job_link) {
      window.open(job.job_link, "_blank");
    }
  };

  // Initialize data on component mount
  useEffect(() => {
    fetchJobOpportunities();
  }, []);

  // Default challenge data if API fails

  return isLocked ? (
    <LockedCard tooltipText="Complete your profile to unlock courses">
      <Card className="h-[38rem] flex flex-col bg-card border border-border rounded-xl shadow-sm">
        <CardHeader className="flex-shrink-0 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-500/20 to-green-500/20 backdrop-blur-sm border border-blue-500/30 rounded-xl flex items-center justify-center">
                <Briefcase className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg font-medium text-foreground">
                  Job Opportunities
                </CardTitle>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent className="flex-1 relative overflow-hidden">
          <div className="h-full overflow-y-auto custom-scrollbar pr-2">
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-3"></div>
            </div>

            <div className="pb-16">
              <div className="space-y-2">
                {jobOpportunities.map((job, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-2 bg-muted rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => handleJobClick(job)}
                    title={`${job.title} at ${job.company} - ${job.type} Level`}
                  >
                    <div className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center">
                      <Briefcase className="w-3 h-3 text-gray-500" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-1">
                        <h4 className="text-sm font-medium text-foreground">
                          {job.title} - {job.company}
                        </h4>
                      </div>
                      <p className="text-xs text-muted-foreground mb-1">
                        {job.type} Level
                      </p>
                      {job.created && (
                        <p className="text-xs text-muted-foreground">
                          Posted: {job.created}
                        </p>
                      )}
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-500" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 pt-4 pb-6 pr-6 pl-6 bg-white pointer-events-none">
            <Button
              variant="outline"
              size="sm"
              className="w-full border-gray-200 hover:bg-gray-50 text-gray-700 pointer-events-auto"
            >
              <Plus className="w-4 h-4 mr-2" />
              View All Jobs
            </Button>
          </div>
        </CardContent>
      </Card>
    </LockedCard>
  ) : (
    <Card className="h-[38rem] flex flex-col bg-card border border-border rounded-xl shadow-sm">
      <CardHeader className="flex-shrink-0 pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-r from-blue-500/20 to-green-500/20 backdrop-blur-sm border border-blue-500/30 rounded-xl flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <CardTitle className="text-lg font-medium text-foreground">
                Job Opportunities
              </CardTitle>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex-1 relative overflow-hidden">
        <div className="h-full overflow-y-auto custom-scrollbar pr-2">
          {apiJobOpportunities.length === 0 ? (
            <div className="flex items-center border-2 rounded-xl  justify-center h-full">
              <p className="text-gray-500 text-sm">No jobs available</p>
            </div>
          ) : (
            <div className="pb-16">
              <div className="space-y-2">
                {apiJobOpportunities.map((job, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-2 bg-muted rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => handleJobClick(job)}
                    title={`${job.job_title} at ${job.company_name} - ${job.level} Level`}
                  >
                    <div className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center">
                      <Briefcase className="w-3 h-3 text-gray-500" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-1">
                        <h4 className="text-sm font-medium text-foreground">
                          {job.job_title} - {job.company_name}
                        </h4>
                      </div>
                      <p className="text-xs text-muted-foreground mb-1">
                        {job.level} Level
                      </p>
                      {job.created && (
                        <p className="text-xs text-muted-foreground">
                          Posted: {job.created}
                        </p>
                      )}
                    </div>
                    <ExternalLink className="w-4 h-4 text-gray-500" />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="absolute bottom-0 left-0 right-0 pt-4 pb-6 pr-6 pl-6 bg-white pointer-events-none">
          <Button
            onClick={() => { navigate("/job-listing"); }}
            variant="outline"
            size="sm"
            disabled={apiJobOpportunities.length === 0}
            className="w-full border-gray-200 hover:bg-gray-50 text-gray-700 pointer-events-auto"
          >
            <Plus className="w-4 h-4 mr-2" />
            View All Jobs
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default JobOpertunites;
